import { useState, useEffect } from "react";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";

/**
 * Resolves the current Firebase user's role for a given business.
 *
 * Role resolution follows a cascading strategy (first match wins):
 *  1. **Venue-level custom claim** — user's ID token `venues` array contains `bizId` → "owner".
 *  2. **Brand-level custom claim** — user's `brands` array includes the venue's `brandId` → "owner".
 *  3. **Staff subcollection (by uid)** — real-time listener on `Business/{bizId}/Staff`
 *     where `uid` matches → uses the `role` field from that document.
 *  4. **Business doc owner fields** — `Business/{bizId}.uid` or `.owner` matches → "owner".
 *  5. **Staff subcollection (by email)** — queries Staff by email, links uid if found → uses that role.
 *  6. **Fallback** — defaults to "owner" on any error or when no record matches.
 *
 * Steps 1–2 rely on custom claims set by `completeClaim` (no Firestore read needed).
 * Step 3 uses `onSnapshot` so the role updates in real time if the Staff doc changes.
 * Step 5 also writes the user's uid onto the Staff doc for faster resolution next time.
 *
 * @param {string} bizId - Firestore document ID of the business.
 * @returns {{
 *   role: ("owner"|"manager"|"staff"|null),
 *   loading: boolean,
 *   isOwner: boolean,
 *   isManager: boolean,
 *   isStaff: boolean,
 *   canEdit: boolean,
 *   canManageMenus: boolean,
 *   canToggle: boolean,
 *   canManageTables: boolean,
 *   canEditFloorPlan: boolean,
 *   canViewOnly: boolean
 * }}
 */
export default function useStaffRole(bizId) {
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe = null;

    if (!bizId) {
      setLoading(false);
      return;
    }

    const user = firebase.auth().currentUser;
    if (!user) {
      setLoading(false);
      return;
    }

    const db = firebase.firestore();

    const runStaffFallback = () => {
      unsubscribe = db
        .collection("Business")
        .doc(bizId)
        .collection("Staff")
        .where("uid", "==", user.uid)
        .onSnapshot(
          (snapshot) => {
            if (cancelled) return;
            if (!snapshot.empty) {
              setRole(snapshot.docs[0].data().role);
              setLoading(false);
            } else {
              db.collection("Business")
                .doc(bizId)
                .get()
                .then((doc) => {
                  if (cancelled) return;
                  if (
                    doc.exists &&
                    (doc.data().uid === user.uid || doc.data().owner === user.uid)
                  ) {
                    setRole("owner");
                    setLoading(false);
                  } else {
                    db.collection("Business")
                      .doc(bizId)
                      .collection("Staff")
                      .where("email", "==", user.email.toLowerCase())
                      .get()
                      .then((emailSnap) => {
                        if (cancelled) return;
                        if (!emailSnap.empty) {
                          const staffDoc = emailSnap.docs[0];
                          staffDoc.ref.update({ uid: user.uid });
                          setRole(staffDoc.data().role);
                        } else {
                          setRole("owner");
                        }
                        setLoading(false);
                      })
                      .catch(() => {
                        if (cancelled) return;
                        setRole("owner");
                        setLoading(false);
                      });
                  }
                })
                .catch(() => {
                  if (cancelled) return;
                  setRole("owner");
                  setLoading(false);
                });
            }
          },
          () => {
            if (cancelled) return;
            setRole("owner");
            setLoading(false);
          }
        );
    };

    user
      .getIdTokenResult(true)
      .then((tokenResult) => {
        if (cancelled) return;
        const venues =
          (tokenResult && tokenResult.claims && tokenResult.claims.venues) || [];

        // Direct venue-level access
        if (Array.isArray(venues) && venues.indexOf(bizId) !== -1) {
          setRole("owner");
          setLoading(false);
          return;
        }

        // Brand-level access: check if user's brand claim covers this venue
        const brands =
          (tokenResult && tokenResult.claims && tokenResult.claims.brands) || [];

        if (Array.isArray(brands) && brands.length > 0) {
          db.collection("Business")
            .doc(bizId)
            .get()
            .then((doc) => {
              if (cancelled) return;
              if (doc.exists && doc.data().brandId && brands.includes(doc.data().brandId)) {
                setRole("owner");
                setLoading(false);
              } else {
                runStaffFallback();
              }
            })
            .catch(() => {
              if (cancelled) return;
              runStaffFallback();
            });
        } else {
          runStaffFallback();
        }
      })
      .catch(() => {
        if (cancelled) return;
        runStaffFallback();
      });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [bizId]);

  return {
    role,
    loading,
    isOwner: role === "owner",
    isManager: role === "manager",
    isStaff: role === "staff",
    canEdit: role === "owner" || role === "manager",
    canManageMenus: role === "owner" || role === "manager",
    canToggle: role === "owner" || role === "manager" || role === "staff",
    canManageTables: role === "owner" || role === "manager",
    canEditFloorPlan: role === "owner" || role === "manager",
    canViewOnly: role === "staff",
  };
}